"use client";

import NextImage from "next/image";
import { useSearchParams } from "next/navigation";
import { useVertexSearch } from "./use-vertex-search";
import { GhostCard } from "./ghost-card";
import { useEffect } from "react";
import { InputSearch } from "@jetblue/dotcom-components/input-search";
import type { SingleUseSearchResultsDialog } from "../../../dialogs/single-use/search-results";
import type { VertexSearchResponse } from "@jetblue/dotcom-api/vertex-search";
import type { UseQueryResult } from "@tanstack/react-query";
import { SearchResult } from "./search-result";

export type SearchResultsFormClientProperties = {
  searchResultsImageAlt: string;
  searchResultsImageSrc: string;
} & Pick<
  SingleUseSearchResultsDialog<string>,
  "primaryLink" | "quickLinks" | "labels" | "apiResponse"
>;

function getQuery(searchParameters: URLSearchParams) {
  // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- For whatever reason, this gives us an "undefined" error in the browser but the linter says this the optional chain is unnecessary
  if (globalThis.location?.hash) {
    const hash = globalThis.location.hash.slice(1);
    const cutoffIndex = hash.indexOf("?");
    return decodeURIComponent(
      cutoffIndex === -1 ? hash : hash.slice(0, Math.max(0, cutoffIndex)),
    );
  }
  return decodeURIComponent(searchParameters.get("search") ?? "");
}

export const SearchResultsFormClient = ({
  quickLinks,
  primaryLink,
  labels,
  searchResultsImageSrc,
  searchResultsImageAlt,
  apiResponse,
}: SearchResultsFormClientProperties) => {
  const searchParameters = useSearchParams();
  const initialQuery = getQuery(searchParameters);

  const {
    setQuery: setSubmittedQuery,
    handleSubmit,
    response,
  }: {
    setQuery: (query: string) => void;
    handleSubmit: (event: React.SubmitEvent<HTMLFormElement>) => void;
    response: UseQueryResult<VertexSearchResponse>;
  } = useVertexSearch({
    initialQuery,
  });

  useEffect(() => {
    if (initialQuery) {
      setSubmittedQuery(initialQuery);
    }
  }, [initialQuery, setSubmittedQuery]);

  const searchResults = response.data?.data.searchResults;

  return (
    <>
      <form
        className="mt-10 flex w-full"
        onSubmit={(event) => {
          handleSubmit(event);
        }}
      >
        <InputSearch
          initialSearch={initialQuery}
          labels={{
            heading: labels.inputHeading,
            placeholder: labels.inputPlaceholder ?? "",
          }}
          primaryLink={primaryLink}
          quickLinks={quickLinks}
          theme="light"
        />
      </form>

      <div className="relative min-h-64 py-12">
        <div aria-live="polite" className="sr-only">
          {response.status == "pending" &&
            apiResponse.screenReaderRequestStatus.loading}
          {response.status == "error" &&
            apiResponse.screenReaderRequestStatus.error}
          {response.status == "success" &&
            apiResponse.screenReaderRequestStatus.success}
        </div>

        {response.isError ||
          (!response.isEnabled && (
            <div
              className="
                relative mt-4 h-64

                md:mt-6
              "
            >
              <NextImage
                alt={searchResultsImageAlt}
                className="mx-auto object-contain"
                fill
                src={searchResultsImageSrc}
              />
            </div>
          ))}

        {response.isEnabled && response.isPending && <GhostCard />}

        {searchResults && (
          <ul className="flex flex-col gap-6">
            {searchResults.map((searchResult) => (
              <SearchResult key={searchResult.id} {...searchResult} />
            ))}
          </ul>
        )}
      </div>
    </>
  );
};
